import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, cx, IconButton } from './primitives.jsx'

/**
 * Overlays and status surfaces.
 *
 * These are the only components in the system permitted to cast a shadow: they
 * genuinely float above the page, which is exactly the case the elevation token
 * is reserved for. Everything else separates with a hairline.
 *
 * 浮层与状态反馈组件。
 *
 * 这些是全系统唯一允许使用投影的组件：它们确实浮在页面之上，而这正是投影令牌被
 * 保留下来的唯一用途。其余一切用发丝线分隔。
 */

/**
 * Modal dialog.
 *
 * Handles the three things a hand-rolled modal always gets wrong: Escape to
 * close, focus moving into the dialog on open and back to the trigger on close,
 * and Tab being trapped inside while it is open.
 *
 * 模态对话框。它处理了手写弹窗总会漏掉的三件事：Escape 关闭、打开时焦点移入
 * 且关闭时归还给触发元素、打开期间 Tab 被限制在弹窗内。
 *
 * @param {object} props Props / 属性。
 * @param {boolean} props.open Whether the dialog is shown / 是否展示。
 * @param {() => void} props.onClose Close handler / 关闭回调。
 * @param {React.ReactNode} props.title Dialog title / 弹窗标题。
 * @param {React.ReactNode} [props.footer] Footer actions / 底部操作区。
 * @param {'sm'|'md'|'lg'} [props.size] Width step / 宽度档位。
 * @param {string} props.closeLabel Accessible label for the close button / 关闭按钮的可访问名称。
 * @returns {JSX.Element|null} Dialog / 弹窗。
 */
export function Modal({ open, onClose, title, footer, size = 'md', closeLabel = 'Close', children }) {
  const panelRef = useRef(null)
  const restoreRef = useRef(null)

  // The setup effect below must run ONCE per open, keyed on `open` alone. Call
  // sites almost always pass an inline arrow — `onClose={() => setOpen(false)}`
  // — which is a fresh identity on every render, so listing it as a dependency
  // re-runs the whole effect on every keystroke inside the dialog: focus is
  // yanked back to the autofocus target, and `restoreRef` is overwritten with
  // whatever was focused at the time, breaking focus restoration on close.
  // Holding the callback in a ref keeps the latest one reachable without making
  // its identity part of the dependency list.
  //
  // 下面的初始化 effect 必须每次「打开」只跑一次，只以 open 为依赖。调用处几乎总是
  // 传内联箭头函数 —— onClose={() => setOpen(false)} —— 每次渲染都是新身份，
  // 若把它列进依赖，弹窗里每敲一个字符整个 effect 就重跑一次：焦点被拽回
  // autofocus 目标，restoreRef 也被当时的焦点元素覆盖，关闭后焦点无法归位。
  // 把回调放进 ref，既能拿到最新的它，又不让它的身份进入依赖列表。
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return undefined

    restoreRef.current = document.activeElement
    // Lock background scrolling; a modal over a scrolling page is disorienting.
    // 锁定背景滚动；弹窗之上还能滚动页面会让人失去方位感。
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current?.()
        return
      }
      if (event.key !== 'Tab' || !panelRef.current) return

      // Focus trap: wrap from last to first and vice versa.
      // 焦点陷阱：从最后一个绕回第一个，反向亦然。
      const focusables = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
      if (!focusables.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)
    // Defer so the panel exists before we reach into it.
    // 延后一帧，等面板挂载后再取焦点。
    const timer = setTimeout(() => {
      const target = panelRef.current?.querySelector('[data-autofocus]') || panelRef.current
      target?.focus?.()
    }, 0)

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      clearTimeout(timer)
      document.body.style.overflow = previousOverflow
      restoreRef.current?.focus?.()
    }
    // `onClose` is deliberately absent — see the ref above. Adding it back
    // reintroduces the focus-stealing bug.
    // 刻意不列 onClose —— 原因见上面的 ref。把它加回来就会重现抢焦点的 bug。
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="ui-modal">
      {/* The scrim is not a button: clicking it closes, but it must not appear
          in the tab order or be announced.
          遮罩不是按钮：点击可关闭，但不应进入 Tab 顺序，也不应被播报。 */}
      <div className="ui-modal__scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className={cx('ui-modal__panel', `ui-modal__panel--${size}`)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        <header className="ui-modal__head">
          <h2 className="ui-modal__title">{title}</h2>
          <IconButton size="sm" label={closeLabel} onClick={onClose}>
            ×
          </IconButton>
        </header>
        <div className="ui-modal__body">{children}</div>
        {footer && <footer className="ui-modal__foot">{footer}</footer>}
      </div>
    </div>,
    document.body
  )
}

/**
 * Confirmation dialog for destructive actions.
 *
 * 破坏性操作的确认弹窗。
 *
 * @param {object} props Props / 属性。
 * @param {boolean} props.open Whether shown / 是否展示。
 * @param {() => void} props.onCancel Cancel handler / 取消回调。
 * @param {() => void} props.onConfirm Confirm handler / 确认回调。
 * @param {React.ReactNode} props.title Title / 标题。
 * @param {React.ReactNode} [props.body] Explanatory text / 说明文字。
 * @param {string} props.confirmLabel Confirm button text / 确认按钮文字。
 * @param {string} props.cancelLabel Cancel button text / 取消按钮文字。
 * @param {boolean} [props.destructive] Use the danger tone / 是否使用危险色调。
 * @returns {JSX.Element|null} Dialog / 弹窗。
 */
export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  body,
  confirmLabel,
  cancelLabel,
  destructive = true,
  busy = false,
}) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      closeLabel={cancelLabel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'danger' : 'primary'} onClick={onConfirm} loading={busy} data-autofocus>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  )
}

const ToastContext = createContext(null)

/**
 * Toast host.
 *
 * Mount once near the root; anything below can call `useToast()`.
 *
 * 提示条宿主。在根附近挂载一次，其下任何组件都可以调用 useToast()。
 *
 * @param {{children: React.ReactNode}} props Children / 子节点。
 * @returns {JSX.Element} Provider / 上下文提供者。
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const counter = useRef(0)

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const push = useCallback(
    (message, tone = 'neutral', ttl = 4000) => {
      counter.current += 1
      const id = counter.current
      setToasts((current) => [...current, { id, message, tone }])
      // Errors stay until dismissed: a failure that vanishes in four seconds is
      // a failure the user never read.
      // 错误提示不自动消失：4 秒就消失的失败提示等于用户没看到。
      if (tone !== 'danger') setTimeout(() => dismiss(id), ttl)
      return id
    },
    [dismiss]
  )

  const value = useMemo(
    () => ({
      push,
      dismiss,
      success: (message) => push(message, 'success'),
      error: (message) => push(message, 'danger'),
      info: (message) => push(message, 'neutral'),
    }),
    [push, dismiss]
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="ui-toasts" role="region" aria-live="polite">
          {toasts.map((toast) => (
            <div key={toast.id} className={cx('ui-toast', `ui-toast--${toast.tone}`)}>
              <span className="ui-toast__message">{toast.message}</span>
              <IconButton size="sm" label="Dismiss" onClick={() => dismiss(toast.id)}>
                ×
              </IconButton>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  )
}

/**
 * Raise a toast from anywhere below the provider.
 *
 * 在 provider 之下的任意位置弹出提示条。
 *
 * @returns {{push: Function, success: Function, error: Function, info: Function, dismiss: Function}} Toast API / 提示条接口。
 */
export function useToast() {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside <ToastProvider>')
  return context
}

/**
 * Empty state.
 *
 * An honest, informative empty state is a better page than a decorative one
 * filled with placeholder cards — it tells the visitor the section is real but
 * not yet populated, and tells an editor what to do about it.
 *
 * 空状态。诚实且有信息量的空状态胜过塞满占位卡片的装饰性页面——它告诉访客
 * 「这个栏目是真的，只是还没有内容」，也告诉编辑该做什么。
 *
 * @param {object} props Props / 属性。
 * @param {React.ReactNode} props.title Headline / 标题。
 * @param {React.ReactNode} [props.body] Explanation / 说明。
 * @param {React.ReactNode} [props.action] Call to action / 操作入口。
 * @returns {JSX.Element} Empty state / 空状态。
 */
export function EmptyState({ title, body, action, className }) {
  return (
    <div className={cx('ui-empty', className)}>
      <p className="ui-empty__title">{title}</p>
      {body && <p className="ui-empty__body">{body}</p>}
      {action && <div className="ui-empty__action">{action}</div>}
    </div>
  )
}

/**
 * Error state with an optional retry.
 *
 * 错误状态，可带重试。
 *
 * @param {object} props Props / 属性。
 * @param {React.ReactNode} props.title Headline / 标题。
 * @param {React.ReactNode} [props.body] Detail / 详情。
 * @param {React.ReactNode} [props.action] Retry control / 重试入口。
 * @returns {JSX.Element} Error state / 错误状态。
 */
export function ErrorState({ title, body, action, className }) {
  return (
    <div className={cx('ui-empty', 'ui-empty--error', className)} role="alert">
      <p className="ui-empty__title">{title}</p>
      {body && <p className="ui-empty__body">{body}</p>}
      {action && <div className="ui-empty__action">{action}</div>}
    </div>
  )
}

/**
 * A standing notice that stays until the condition behind it is fixed.
 *
 * Deliberately not a toast. A toast is right for "that worked" and wrong for
 * anything the reader has to *do*, because it leaves on a timer whether or not
 * they acted — and the message most worth showing is the one someone has been
 * scrolling past for a week. This has no dismiss control for the same reason:
 * the way to make it go away is to fix what it is reporting.
 *
 * 一条常驻通知，条件解除前不会消失。
 *
 * 刻意不做成 toast。toast 适合「成功了」，不适合任何需要读者去「做」的事——
 * 因为它到点就走，不管人有没有动手；而最值得展示的那条消息，往往正是已经被
 * 划过去一整周的那条。它同样没有关闭按钮，理由相同：让它消失的办法，
 * 是把它所报告的问题解决掉。
 *
 * @param {object} props Props / 属性。
 * @param {'warning'|'danger'} [props.tone] Severity / 严重程度。
 * @param {React.ReactNode} props.title Headline / 标题。
 * @param {React.ReactNode} [props.body] Explanation / 说明。
 * @param {React.ReactNode} [props.action] Call to action / 操作入口。
 * @param {string} [props.className] Extra class / 额外类名。
 * @returns {JSX.Element} Notice / 通知条。
 */
export function Notice({ tone = 'warning', title, body, action, className }) {
  return (
    <div className={cx('ui-notice', `ui-notice--${tone}`, className)} role="alert">
      <div className="ui-notice__text">
        <p className="ui-notice__title">{title}</p>
        {body && <p className="ui-notice__body">{body}</p>}
      </div>
      {action && <div className="ui-notice__action">{action}</div>}
    </div>
  )
}
